import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

export default function DriveConnectComplete() {
  const navigate = useNavigate();
  const outcome = (getQueryParam('status') || 'success').toLowerCase();
  const state = getQueryParam('state') || undefined;
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isSuccess = outcome === 'success';

  // On success, redirect back to trigger the auth retry flow
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isSuccess && !isRedirecting) {
      // Mark that Drive connect completed successfully
      sessionStorage.setItem('drive_connect_completed', 'true');
      if (state) {
        sessionStorage.setItem('drive_connect_completed_state', state);
      }
      setIsRedirecting(true);
      // Redirect to root - AuthProvider will detect the completed flag and retry
      navigate('/', { replace: true });
    }
  }, [isSuccess, isRedirecting, state, navigate]);

  const message = useMemo(() => {
    if (isSuccess) {
      return 'Drive access granted. Redirecting to complete login...';
    }
    if (outcome === 'cancelled') {
      return 'Drive access was cancelled. Click below to return to login and try again.';
    }
    return 'We could not confirm Drive access. Click below to return to login and try again.';
  }, [isSuccess, outcome]);

  // Show loading state when redirecting on success
  if (isSuccess && isRedirecting) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <CircularProgress size={48} />
          <Typography variant="h5">Drive connection complete</Typography>
          <Typography color="text.secondary">Redirecting to complete login...</Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Stack spacing={3} alignItems="center" textAlign="center">
        <Box color={isSuccess ? 'success.main' : 'warning.main'}>
          {isSuccess ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 64 }} />
          ) : (
            <WarningAmberIcon sx={{ fontSize: 64 }} />
          )}
        </Box>
        <Typography variant="h5">
          {isSuccess ? 'Drive connection complete' : 'Drive connection needs attention'}
        </Typography>
        <Typography color="text.secondary">{message}</Typography>
        {state ? (
          <Typography variant="body2" color="text.disabled">
            State reference: {state}
          </Typography>
        ) : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={() => navigate('/', { replace: true })}>
            Return to login
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
